// Client for the Worker's /v1/secrets API (apps/worker/src/secrets.ts).
// Uses fetch directly: these calls carry no account credentials, and the
// sender token goes in a header, never in a URL.

import type {
  ApiErrorPayload,
  CreateSecretRequest,
  CreateSecretResponse,
  RevealSecretResponse,
  SecretMetadata,
  SecretStatus,
} from '@sss/types';
import { API_BASE_URL } from './config';

export type SecretsApiErrorType = 'gone' | 'rateLimited' | 'tooLarge' | 'challengeFailed' | 'passPending' | 'network' | 'other';

export class SecretsApiError extends Error {
  constructor(
    readonly type: SecretsApiErrorType,
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'SecretsApiError';
  }
}

const errorType = (status: number, type: string | undefined): SecretsApiErrorType => {
  if (status === 404 && type === 'gone') return 'gone';
  if (status === 404 && type === 'passPending') return 'passPending';
  if (status === 429) return 'rateLimited';
  if (status === 413) return 'tooLarge';
  if (status === 403 && type === 'challengeFailed') return 'challengeFailed';
  return 'other';
};

/** A call to the edge Worker; errors become SecretsApiError. Shared with roomsApi.ts. */
export const request = async <T>(path: string, init: RequestInit = {}, base = API_BASE_URL): Promise<T> => {
  let res: Response;
  try {
    // no-referrer: the page URL must never leave in a Referer header (it isn't secret today, but cheap insurance).
    res = await fetch(`${base}${path}`, { ...init, referrerPolicy: 'no-referrer', cache: 'no-store' });
  } catch {
    throw new SecretsApiError('network', 0, 'Network error');
  }
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const payload = (body ?? {}) as Partial<ApiErrorPayload>;
    throw new SecretsApiError(errorType(res.status, payload.type), res.status, payload.message ?? `HTTP ${res.status}`);
  }
  return body as T;
};

export const json = (method: string, body: unknown): RequestInit => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
const asSender = (senderToken: string, method = 'GET'): RequestInit => ({ method, headers: { Authorization: `Bearer ${senderToken}` } });
const path = (id: string, suffix = '') => `/v1/secrets/${encodeURIComponent(id)}${suffix}`;

export const createSecret = (input: CreateSecretRequest, base?: string) => request<CreateSecretResponse>('/v1/secrets', json('POST', input), base);

/** Metadata only; never consumes a view. */
export const getSecret = (id: string, base?: string) => request<SecretMetadata>(path(id), {}, base);

/** Consumes one view. Call only on an explicit user action. */
export const revealSecret = (id: string, base?: string) => request<RevealSecretResponse>(path(id, '/reveal'), { method: 'POST' }, base);

export const getSecretStatus = (id: string, senderToken: string, base?: string) => request<SecretStatus>(path(id, '/status'), asSender(senderToken), base);

export const deleteSecret = (id: string, senderToken: string, base?: string) => request<void>(path(id), asSender(senderToken, 'DELETE'), base);
